import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useProjectSelection } from './ProjectSelectionContext';

type FocusContextValue = {
  /** Currently selected project guid (single selection) */
  selectedProjectGuid: string | null;
  /** A transient guid used to trigger marker pulse animation */
  pulseProjectGuid: string | null;
  /** Selects a project (optionally pulsing it). */
  selectProject: (projectGuid: string, opts?: { pulse?: boolean }) => void;
  /** Pulses a project once without changing selection. */
  pulseOnce: (projectGuid: string) => void;
};

const FocusContext = createContext<FocusContextValue | null>(null);

/**
 * FocusProvider
 *
 * Thin layer on top of ProjectSelectionContext.
 *
 * Purpose:
 * - Centralize selection + pulse behavior so Map / Grid / Navigator can share it.
 * - Keep MyProjectsContent handlers small and consistent.
 *
 * This intentionally does NOT own tab index or KPI focus yet (those remain in the
 * existing flow), but it provides the core interaction primitives we need.
 */
export function FocusProvider({ children }: { children: React.ReactNode }) {
  const {
    selectedProjectGuid,
    setSelectedProjectGuid,
    pulseProjectGuid,
    setPulseProjectGuid,
  } = useProjectSelection();

  const pulseOnce = useCallback(
    (guid: string) => {
      const g = String(guid || '');
      if (!g) return;
      setPulseProjectGuid(g);
      // Clear the pulse after the CSS animation has had time to run.
      window.setTimeout(() => {
        setPulseProjectGuid((prev) => (prev === g ? null : prev));
      }, 1600);
    },
    [setPulseProjectGuid]
  );

  const selectProject = useCallback(
    (guid: string, opts?: { pulse?: boolean }) => {
      const g = String(guid || '');
      if (!g) return;
      setSelectedProjectGuid(g);
      if (opts?.pulse) pulseOnce(g);
    },
    [pulseOnce, setSelectedProjectGuid]
  );

  const value = useMemo<FocusContextValue>(
    () => ({
      selectedProjectGuid: selectedProjectGuid || null,
      pulseProjectGuid: pulseProjectGuid || null,
      selectProject,
      pulseOnce,
    }),
    [pulseProjectGuid, pulseOnce, selectProject, selectedProjectGuid]
  );

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
}

export function useFocus() {
  const ctx = useContext(FocusContext);
  if (!ctx) {
    throw new Error('useFocus must be used within a FocusProvider');
  }
  return ctx;
}
